import React, { useState, useEffect } from 'react';
import { Typography, Space, Button, Table, Select, Input, Breadcrumb, Tag, Spin } from 'antd';
import { showConfirmModal } from '../components/PopupModal';
import { PlusOutlined, SearchOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { useNavigate } from 'react-router-dom';
import noticeService from '../services/notices';
import {
  NoticeItem,
  NoticeFilterOptions,
  getExposureDisplayText,
  getApprovalStatusDisplayText
} from '../types/notice';
import { message } from '../utils/message';

const { Title, Text } = Typography;
const { Option } = Select;

const Notices: React.FC = () => {
  const navigate = useNavigate();
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [loading, setLoading] = useState(false);
  const [notices, setNotices] = useState<NoticeItem[]>([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
    totalPages: 0
  });
  const [filters, setFilters] = useState<NoticeFilterOptions>({
    page: 1,
    limit: 10,
    exposure: 'all',
    approvalStatus: 'all',
    search: ''
  });

  const columns: ColumnsType<NoticeItem> = [
    {
      title: 'No.',
      key: 'no',
      width: 80,
      align: 'center',
      render: (_text: any, _record: NoticeItem, index: number) => {
        return (pagination.current - 1) * pagination.pageSize + index + 1;
      },
    },
    {
      title: '노출여부',
      dataIndex: 'exposure',
      key: 'exposure',
      width: 100,
      align: 'center',
      render: (exposure: string) => getExposureDisplayText(exposure as any)
    },
    {
      title: '제목',
      dataIndex: 'title',
      key: 'title',
      ellipsis: true,
    },
    {
      title: '관리자명',
      key: 'manager',
      width: 120,
      align: 'center',
      render: (_text: any, record: NoticeItem) => record.creator?.name || '-'
    },
    {
      title: '등록일',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 120,
      align: 'center',
      render: (_text: any, record: NoticeItem) => {
        const dateStr = record.createdAt || (record as any).created_at;
        if (!dateStr) return '-';
        try {
          const date = new Date(dateStr);
          if (isNaN(date.getTime())) return '-';
          return date.toLocaleDateString('ko-KR');
        } catch (error) {
          return '-';
        }
      }
    },
    {
      title: '상태',
      dataIndex: 'approvalStatus',
      key: 'approvalStatus',
      width: 100,
      align: 'center',
      render: (status: string) => {
        const text = getApprovalStatusDisplayText(status as any);
        if (text === '승인대기') {
          return <Tag color="default">{text}</Tag>;
        } else if (text === '승인완료') {
          return <Tag color="blue">{text}</Tag>;
        } else if (text === '승인반려') {
          return <Tag color="red">{text}</Tag>;
        }
        return <Tag color="blue">{text}</Tag>;
      },
    },
  ];

  // 데이터 로딩 함수
  const loadNotices = async () => {
    try {
      setLoading(true);
      const response = await noticeService.getNoticeList(filters);
      setNotices(response.data.notices);
      setPagination({
        current: response.data.pagination.page,
        pageSize: response.data.pagination.limit,
        total: response.data.pagination.total,
        totalPages: response.data.pagination.totalPages
      });
    } catch (error) {
      console.error('공지사항 목록 로딩 실패:', error);
      message.error('공지사항 목록을 불러오는데 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  // 초기 데이터 로딩
  useEffect(() => {
    loadNotices();
  }, [filters]);

  // 필터 변경 핸들러
  const handleFilterChange = (key: keyof NoticeFilterOptions, value: any) => {
    setFilters(prev => ({
      ...prev,
      [key]: value,
      page: 1 // 필터 변경 시 첫 페이지로 이동
    }));
  };

  // 검색 핸들러
  const handleSearch = (value: string) => {
    handleFilterChange('search', value);
  };

  // 페이지네이션 변경 핸들러
  const handleTableChange = (page: number, pageSize?: number) => {
    setFilters(prev => ({
      ...prev,
      page,
      limit: pageSize || prev.limit
    }));
  };



  // 일괄 삭제 처리
  const handleBulkDelete = async () => {
    if (selectedRowKeys.length === 0) {
      message.warning('삭제할 공지사항을 선택해주세요.');
      return;
    }

    showConfirmModal({
      title: '알림',
      content: `삭제한 콘텐츠는 복구할 수 없습니다.\n삭제하시겠습니까?`,
      okText: '확인',
      okType: 'danger',
      onConfirm: async () => {
        try {
          const ids = selectedRowKeys.map(key => Number(key));
          await noticeService.bulkDeleteNotices(ids);
          message.success(`삭제가 완료되었습니다.`);
          setSelectedRowKeys([]);
          await loadNotices();
        } catch (error) {
          console.error('일괄 삭제 실패:', error);
          message.error('공지사항 삭제에 실패했습니다.');
        }
      },
    });
  };

  // 일괄 노출/비노출 처리
  const handleBulkExposureChange = async (exposure: 'show' | 'hide') => {
    if (selectedRowKeys.length === 0) {
      message.warning('변경할 공지사항을 선택해주세요.');
      return;
    }

    try {
      const ids = selectedRowKeys.map(key => Number(key));
      await noticeService.bulkUpdateExposure(ids, exposure);
      message.success(exposure === "show" ? "노출 처리 되었습니다." : "비노출 처리 되었습니다.");
      setSelectedRowKeys([]);
      await loadNotices();
    } catch (error) {
      console.error('일괄 노출 상태 변경 실패:', error);
      message.error('노출 상태 변경에 실패했습니다.');
    }
  };

  const onSelectChange = (newSelectedRowKeys: React.Key[]) => {
    setSelectedRowKeys(newSelectedRowKeys);
  };

  const rowSelection = {
    selectedRowKeys,
    onChange: onSelectChange,
  };

  return (
    <Spin spinning={loading}>
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        {/* 브레드크럼 */}
        <Breadcrumb
          items={[
            { title: '콘텐츠 관리' },
            { title: '건물 안내' },
            { title: '공지사항' }
          ]}
          style={{ marginBottom: '8px' }}
        />

        {/* 페이지 제목 */}
        <Title level={2} style={{ margin: '0 0 32px 0' }}>
          공지사항
        </Title>

        {/* 필터 및 검색 */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '24px'
        }}>
          <div style={{ display: 'flex', gap: '16px', alignItems: 'center' }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
              <Text style={{ fontSize: '12px', color: '#a6a6a6' }}>노출여부</Text>
              <Select
                value={filters.exposure}
                style={{ width: 120 }}
                onChange={(value) => handleFilterChange('exposure', value)}
              >
                <Option value="all">전체</Option>
                <Option value="show">노출</Option>
                <Option value="hide">비노출</Option>
              </Select>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
              <Text style={{ fontSize: '12px', color: '#a6a6a6' }}>상태</Text>
              <Select
                value={filters.approvalStatus}
                style={{ width: 120 }}
                onChange={(value) => handleFilterChange('approvalStatus', value)}
              >
                <Option value="all">전체</Option>
                <Option value="pending">승인대기</Option>
                <Option value="approved">승인완료</Option>
                <Option value="rejected">승인반려</Option>
              </Select>
            </div>
          </div>
          <Button type="primary" icon={<PlusOutlined />} onClick={() => navigate('/notices/add')}>
            추가
          </Button>
        </div>

        {/* 구분선 */}
        <div style={{
          height: '1px',
          backgroundColor: '#303030',
          margin: '24px 0'
        }} />

        {/* 테이블 상단 정보 */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '16px'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            <Text>총 {pagination.total}건</Text>
            <Button
              onClick={handleBulkDelete}
              disabled={selectedRowKeys.length === 0}
              danger
              size="middle"
            >
              삭제
            </Button>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <Button
              onClick={() => handleBulkExposureChange('hide')}
              disabled={selectedRowKeys.length === 0}
              size="middle"
            >
              비노출
            </Button>
            <Button
              onClick={() => handleBulkExposureChange('show')}
              disabled={selectedRowKeys.length === 0}
              size="middle"
            >
              노출
            </Button>
            <Input
              placeholder="검색"
              suffix={<SearchOutlined style={{ color: '#a6a6a6' }} />}
              style={{
                width: 400,
                height: 40,
                borderRadius: 8,
                background: '#2a2a2a',
                border: '1px solid #404040',
                color: '#ffffff'
              }}
              onChange={(e) => handleSearch(e.target.value)}
              value={filters.search}
              allowClear
            />
          </div>
        </div>

        {/* 테이블 */}
        <Table
          rowSelection={rowSelection}
          columns={columns}
          dataSource={notices.map(notice => ({ ...notice, key: notice.id.toString() }))}
          onRow={(record) => ({
            onClick: () => {
              navigate(`/notices/detail/${record.id}`);
            },
            style: { cursor: 'pointer' }
          })}
          pagination={{
            current: pagination.current,
            total: pagination.total,
            pageSize: pagination.pageSize,
            showSizeChanger: true,
            showQuickJumper: false,
            pageSizeOptions: ['10', '20', '50'],
            style: { textAlign: 'right' },
            onChange: handleTableChange,
            onShowSizeChange: handleTableChange,
          }}
          style={{
            backgroundColor: '#1f1f1f',
          }}
          size="middle"
        />
      </Space>
    </Spin>
  );
};

export default Notices;